import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from 'axios';
import { getSession, signOut } from 'next-auth/react';
import { GITHUB_ACCESS_TOKEN_COOKIE } from './constants';
import useUserContextStore from '@/contexts/UserContext';
import { useAuthStore } from '@/state/authState';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import { getQueryClient } from '@/lib/queryClient';
import {
  serverOrgQueryKey,
  serverUserQueryKey,
} from '@/hooks/useServerIdentity';
import apiUrl from '@/apis/api';
import {
  getStreamToken,
  StreamTokenError,
  clearStreamTokenCache,
} from './streamAuth';

// Every src/apis/* file builds its request URL by prefixing this same
// `apiUrl` constant (the absolute Django origin) -- axios's own `baseURL`
// option is never populated by any caller, so it has no effect on where
// requests go. Rewriting the URL here, at the two places a request actually
// gets sent, is the one chokepoint that reaches every caller without editing
// each of the 280+ files in src/apis/. Streaming calls (postStreamWithAuth)
// deliberately skip this and keep the absolute Django URL: proxying a
// multi-minute SSE response through a Vercel function would cap it at 180s.
const toBffUrl = (url: string): string => {
  if (!url.startsWith(apiUrl)) return url;
  const rest = url.slice(apiUrl.length);
  // Strip the trailing slash before the query string. Next 308-redirects
  // "/api/be/x/" to "/api/be/x" (default trailingSlash: false), so keeping it
  // makes every single API call pay an extra round trip and show up twice in
  // the network tab. The BFF re-appends the slash server-side for Django.
  const queryIndex = rest.indexOf('?');
  const path = queryIndex === -1 ? rest : rest.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : rest.slice(queryIndex);
  return `/api/be/${path.replace(/\/$/, '')}${query}`;
};

let isRedirecting = false; // Prevent multiple redirects

// The root layout seeds SessionProvider from the cookie, so the browser never
// calls /api/auth/session on its own and the NextAuth jwt callback, which
// re-exchanges an expiring backend JWT and rewrites the cookie, never runs.
// A 401 is the moment to ask: one shared read for a burst of 401s.
let sessionHeal: Promise<unknown> | null = null;
const healSession = (): Promise<unknown> => {
  sessionHeal ??= getSession().finally(() => {
    sessionHeal = null;
  });
  return sessionHeal;
};

// A healed 401 means this page was server-rendered with an expired backend
// JWT, so the dashboard layout hydrated the identity keys as null. Their
// staleTime keeps that null for minutes; mark them stale now that the cookie
// works. Resolved entries are left alone.
const refetchNullIdentity = (): void => {
  const queryClient = getQueryClient();
  for (const queryKey of [serverUserQueryKey, serverOrgQueryKey]) {
    if (queryClient.getQueryData(queryKey) === null) {
      void queryClient.invalidateQueries({ queryKey, exact: true });
    }
  }
};

// True while the user is sitting on an auth page, where there is no session to
// lose. Signing out from here cannot help: signOut() triggers a full page load,
// which resets every module-level and useRef guard, so any component that fires
// an authenticated request on mount would 401 again and sign out again forever.
// Persisted Zustand state outliving a logout is enough to start that loop.
const isOnAuthPage = (): boolean => {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return (
    pathname.startsWith('/login') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/api/auth')
  );
};

// Build a /login URL that preserves the current path so the user is sent back
// to where they were after re-authenticating. Skips auth pages to avoid loops.
const buildLoginRedirectUrl = (): string => {
  if (typeof window === 'undefined') return '/login';
  const { pathname, search, hash } = window.location;
  const current = `${pathname}${search}${hash}`;
  if (!current || current === '/' || isOnAuthPage()) return '/login';
  return `/login?callbackURLAfterSignIn=${encodeURIComponent(current)}`;
};

// Reset redirect flag on page load (in case user navigated back)
if (typeof window !== 'undefined') {
  // Reset redirect flag after a short delay to handle page reloads
  window.addEventListener('load', () => {
    setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  });
}

// Types for the adapter
export interface HttpAdapterConfig {
  baseURL?: string;
  timeout?: number;
  withCredentials?: boolean;
}

export interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
}

export interface AxiosRequestConfigWithAuth extends AxiosRequestConfig {
  skipAuth?: boolean;
  _retry?: boolean;
}

// Identification metadata headers attached to every authenticated request.
// Was previously a zod schema parsed at runtime; every field here is already
// a plain string (or omitted) by construction below, so the runtime parse
// added no safety over what TypeScript already guarantees -- a plain type
// keeps the same shape without pulling zod into the app-shell bundle.
export interface IdentificationHeaders {
  Useruuid?: string;
  Useremail?: string;
  Username?: string;
  Orguuid?: string;
  Orgname?: string;
  Pageurl?: string;
  'Content-Type'?: string;
  ProviderName?: string;
  Authorization?: string;
  Selfhosted?: string;
  JWT?: string;
  Role?: string;
  'x-request-id'?: string;
}

function sanitizeProviderNameHeader(provider: string | undefined): string {
  if (typeof provider !== 'string') return '';
  const t = provider.trim();
  if (!t || !/^[\w-]+$/.test(t)) return '';
  return t;
}

// Global axios instance with interceptors
let axiosInstance: AxiosInstance | null = null;

// Exported so callers that go direct to the backend (bypassing both
// postWithAuth/fetchWithAuth and postStreamWithAuth) can still attach the
// same identification headers -- see userSyncService.ts's plain-fetch call.
export const addIdentificationMetadata = (
  headers: AxiosRequestHeaders
): Partial<IdentificationHeaders> => {
  const user = useUserContextStore.getState()?.user;
  const organization = useUserContextStore.getState()?.organization;
  const authUrl = useAuthStore.getState().authUrl;
  const role = user?.Role;

  if (user) {
    // Generate a unique request ID for tracing this request end-to-end
    const requestId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;

    // Authorization is no longer attached here: requests to our backend route
    // through the BFF (/api/be/*), which attaches its own Bearer token
    // server-side from the httpOnly session cookie.
    const authHeaders: Partial<
      IdentificationHeaders | AxiosRequestHeaders | undefined
    > = {
      ...headers,
      Useruuid: user?.UserUUID,
      Useremail: user?.email || '',
      Username: user?.username || '',
      Orguuid: user?.OrganizationUUID,
      Orgname: organization?.OrgName || '',
      // Never send Access-Control-* on requests — they are response headers only.
      // Including them forces preflight to ask for them and can break CORS.
      'Content-Type': 'application/json',
      ProviderName: sanitizeProviderNameHeader(user?.provider),
      Pageurl: typeof window !== 'undefined' ? window.location.href : '',
      Selfhosted: authUrl,
      'x-request-id': requestId,
    };

    // Filter out empty/undefined values
    const filteredHeaders = Object.entries(authHeaders).reduce(
      (acc, [key, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
          acc[key as keyof IdentificationHeaders] = value;
        }
        return acc;
      },
      {} as Partial<IdentificationHeaders>
    );

    return filteredHeaders;
  }

  return {};
};

// Create axios instance with interceptors
export const createAxiosInstance = (
  config?: HttpAdapterConfig
): AxiosInstance => {
  if (axiosInstance) {
    return axiosInstance;
  }

  axiosInstance = axios.create({
    // No caller ever passes baseURL (createAxiosInstance() is always called
    // with no args), so this is never populated. Every request URL comes in
    // absolute already; the request interceptor below rewrites it via
    // toBffUrl instead of relying on this option.
    baseURL: config?.baseURL,
    timeout: config?.timeout || 30000,
    withCredentials: config?.withCredentials || false,
  });

  // Request interceptor to add Authorization header and metadata
  axiosInstance.interceptors.request.use(
    (config) => {
      const configWithAuth = config as AxiosRequestConfigWithAuth;
      if (configWithAuth.skipAuth) {
        return config;
      }

      config.headers = config.headers || {};

      // Route browser calls to our backend through the BFF proxy so the
      // httpOnly session cookie authenticates instead of a token from JS.
      if (config.url) {
        config.url = toBffUrl(config.url);
      }

      // Add identification metadata headers
      const metadata = addIdentificationMetadata(config.headers);
      Object.entries(metadata).forEach(([key, value]) => {
        // Only set if value is valid (not null, undefined, or empty string)
        if (value !== null && value !== undefined && value !== '') {
          config.headers![key] = value;
        }
      });

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config;

      // A 401 from the BFF usually means the backend JWT in the session
      // cookie expired. Re-read the session once (see healSession) and retry;
      // only a second 401 means the session is really dead -- sign out and
      // send the user back to login.
      if (error.response?.status === 401) {
        // Skip for login/auth endpoints to prevent loops
        const isAuthEndpoint =
          originalRequest.url?.includes('/auth/') ||
          originalRequest.url?.includes('/login') ||
          originalRequest.url?.includes('/exchange');

        if (!isAuthEndpoint && !originalRequest._retry) {
          originalRequest._retry = true;
          await healSession();
          const retried = await createAxiosInstance()(originalRequest);
          refetchNullIdentity();
          return retried;
        }

        if (isAuthEndpoint || isOnAuthPage()) {
          // For auth endpoints, don't redirect - just reject
          return Promise.reject(error);
        }

        // Clear auth and redirect to login (only once for a burst of 401s)
        if (typeof window !== 'undefined' && !isRedirecting) {
          isRedirecting = true;

          // Clear all auth state (clearAllAuth already clears JWT, so no need for separate clearJWT call)
          useAuthStore.getState().clearAllAuth();
          useOrganizationStore.getState().clearOrganization();
          useUserContextStore.getState().reset();
          useUserStore.getState().clearUser();

          // Show user-friendly error message
          const errorMessage =
            'Please sign in again. If this keeps happening, our service may be temporarily unavailable.';
          sessionStorage.setItem('authError', errorMessage);

          // Sign out from NextAuth to clear the session cookie (this is the critical fix)
          // Use redirect: true to let NextAuth handle redirect after session is fully cleared
          const loginRedirectUrl = buildLoginRedirectUrl();
          try {
            await signOut({ redirect: true, callbackUrl: loginRedirectUrl });
          } catch (signOutError) {
            console.error('Failed to sign out from NextAuth:', signOutError);
            // Fallback: if signOut fails, manually redirect
            window.location.href = loginRedirectUrl;
          }

          // Reset redirect flag after navigation (in case user comes back)
          setTimeout(() => {
            isRedirecting = false;
          }, 2000);
        }
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

// Get the default axios instance
export const getAxiosInstance = (): AxiosInstance => {
  return createAxiosInstance();
};

/** Browser fetch often throws TypeError("Failed to fetch") with no detail — surface likely causes. */
async function fetchOrExplainNetworkError(
  url: string,
  init: RequestInit
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    if (
      err instanceof TypeError &&
      typeof window !== 'undefined' &&
      (err.message === 'Failed to fetch' ||
        err.message.includes('Load failed') ||
        err.message.includes('NetworkError'))
    ) {
      let origin = 'API';
      try {
        origin = new URL(url).origin;
      } catch {
        /* ignore invalid URL */
      }
      throw new Error(
        `Failed to fetch ${origin}. Is the backend running? Match NEXT_PUBLIC_LOCAL_ENDPOINT (dev) or NEXT_PUBLIC_PRODUCTION_ENDPOINT to that URL. HTTPS pages cannot use http:// APIs (mixed content). CORS must allow this origin.`
      );
    }
    throw err;
  }
}

// Fetch wrapper with automatic Authorization header and metadata
export const fetchWithAuth = async (
  url: string,
  config: RequestConfig = {}
): Promise<Response> => {
  const { skipAuth, ...fetchConfig } = config;

  // Route browser calls to our backend through the BFF proxy so the httpOnly
  // session cookie authenticates instead of a token from JS.
  const targetUrl = toBffUrl(url);

  const headers = new Headers(fetchConfig.headers);

  if (!skipAuth) {
    // Add identification metadata headers
    const metadata = addIdentificationMetadata(
      headers as unknown as AxiosRequestHeaders
    );
    Object.entries(metadata).forEach(([key, value]) => {
      // Only set if value is valid (not null, undefined, or empty string)
      if (value !== null && value !== undefined && value !== '') {
        headers.set(key, String(value));
      }
    });
  }

  const requestInit: RequestInit = {
    ...fetchConfig,
    headers,
    signal: fetchConfig.signal
      ? AbortSignal.any([fetchConfig.signal, AbortSignal.timeout(600000)])
      : AbortSignal.timeout(600000),
  };
  let response = await fetchOrExplainNetworkError(targetUrl, requestInit);

  // A 401 from the BFF usually means the backend JWT in the session cookie
  // expired. Re-read the session once (see healSession) and retry; only a
  // second 401 means the session is really dead -- sign out and send the
  // user back to login.
  if (response.status === 401 && !skipAuth) {
    // Skip for login/auth endpoints to prevent loops
    const isAuthEndpoint =
      url.includes('/auth/') ||
      url.includes('/login') ||
      url.includes('/exchange');

    if (!isAuthEndpoint) {
      await healSession();
      response = await fetchOrExplainNetworkError(targetUrl, requestInit);
      if (response.status !== 401) {
        refetchNullIdentity();
        return response;
      }
    }

    if (isAuthEndpoint || isOnAuthPage()) {
      // For auth endpoints, don't redirect - return the error response
      return response;
    }

    // Clear auth and redirect to login (only once for a burst of 401s)
    if (typeof window !== 'undefined' && !isRedirecting) {
      isRedirecting = true;

      // Clear all auth state (clearAllAuth already clears JWT, so no need for separate clearJWT call)
      useAuthStore.getState().clearAllAuth();
      useUserContextStore.getState().clearUser();
      useUserContextStore.getState().clearOrganization();
      useUserStore.getState().clearUser();

      // Show user-friendly error message
      const errorMessage =
        'Please sign in again. If this keeps happening, our service may be temporarily unavailable.';
      sessionStorage.setItem('authError', errorMessage);

      // Sign out from NextAuth to clear the session cookie (this is the critical fix)
      // Use redirect: true to let NextAuth handle redirect after session is fully cleared
      const loginRedirectUrl = buildLoginRedirectUrl();
      try {
        await signOut({ redirect: true, callbackUrl: loginRedirectUrl });
      } catch (signOutError) {
        console.error('Failed to sign out from NextAuth:', signOutError);
        // Fallback: if signOut fails, manually redirect
        window.location.href = loginRedirectUrl;
      }

      // Reset redirect flag after navigation (in case user comes back)
      setTimeout(() => {
        isRedirecting = false;
      }, 2000);
    }
  }

  return response;
};

// POST wrapper
export const postWithAuth = async (
  url: string,
  data?: any,
  config: RequestConfig = {}
): Promise<Response> => {
  return fetchWithAuth(url, {
    method: 'POST',
    headers: {
      ...config.headers,
    },
    body: data === 'string' ? data : JSON.stringify(data),
    ...config,
  });
};

/**
 * Session-expired handling for a failed stream-token mint. Mirrors the axios
 * response interceptor's 401 flow above: a dead session looks the same
 * whether the BFF or the token-mint endpoint is the one that reports it, so
 * this reuses buildLoginRedirectUrl + signOut the same way instead of
 * quietly surfacing a generic fetch error to the caller.
 */
const redirectToLoginAfterAuthFailure = async (): Promise<void> => {
  if (typeof window === 'undefined' || isRedirecting || isOnAuthPage()) return;
  isRedirecting = true;

  useAuthStore.getState().clearAllAuth();
  useOrganizationStore.getState().clearOrganization();
  useUserContextStore.getState().reset();
  useUserStore.getState().clearUser();

  sessionStorage.setItem(
    'authError',
    'Please sign in again. If this keeps happening, our service may be temporarily unavailable.'
  );

  const loginRedirectUrl = buildLoginRedirectUrl();
  try {
    await signOut({ redirect: true, callbackUrl: loginRedirectUrl });
  } catch (signOutError) {
    console.error('Failed to sign out from NextAuth:', signOutError);
    window.location.href = loginRedirectUrl;
  }

  setTimeout(() => {
    isRedirecting = false;
  }, 2000);
};

/**
 * POST to a streaming endpoint, bypassing the BFF proxy.
 *
 * Streaming responses must not go through /api/be, because a Vercel function
 * caps how long it can hold a connection open and several of these streams run
 * for minutes. The call therefore goes straight to the backend with a
 * short-lived scoped token instead of the session cookie.
 */
export const postStreamWithAuth = async (
  absoluteUrl: string,
  body: unknown,
  init: RequestInit = {}
): Promise<Response> => {
  const headers = new Headers(init.headers);

  // Same identification metadata the axios path sends, so backend middleware
  // can attribute a streamed request instead of logging a blank
  // uuid/orgUUID/userEmail/request_id.
  const metadata = addIdentificationMetadata(
    headers as unknown as AxiosRequestHeaders
  );
  Object.entries(metadata).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      headers.set(key, String(value));
    }
  });
  headers.set('Content-Type', 'application/json');

  // Mints (or reuses the cached) stream token and sends the request. Used
  // once up front, then once more on a stale-token retry below.
  const mintAndSend = async (): Promise<Response> => {
    let jwt: string;
    try {
      jwt = await getStreamToken();
    } catch (err) {
      // A 401/403 means the session itself is dead, same as a BFF 401 -- sign
      // out instead of letting the caller surface a generic mint error.
      if (
        err instanceof StreamTokenError &&
        (err.status === 401 || err.status === 403)
      ) {
        await redirectToLoginAfterAuthFailure();
      }
      throw err;
    }
    headers.set('Authorization', `Bearer ${jwt}`);

    return fetchOrExplainNetworkError(absoluteUrl, {
      ...init,
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  };

  const response = await mintAndSend();

  // The stream token is cached for ~10 minutes. If the backend rejects it
  // (expired early, revoked, org switched, ...) clear the cache and retry
  // once with a freshly minted token instead of blocking every stream call
  // for the rest of the cache window.
  if (response.status === 401 || response.status === 403) {
    clearStreamTokenCache();
    const retryResponse = await mintAndSend();
    if (retryResponse.status === 401 || retryResponse.status === 403) {
      await redirectToLoginAfterAuthFailure();
    }
    return retryResponse;
  }

  return response;
};

// GET wrapper
export const getWithAuth = async (
  url: string,
  config: RequestConfig = {}
): Promise<Response> => {
  return fetchWithAuth(url, {
    method: 'GET',
    ...config,
  });
};

// PUT wrapper
export const putWithAuth = async (
  url: string,
  data?: any,
  config: RequestConfig = {}
): Promise<Response> => {
  return fetchWithAuth(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
    ...config,
  });
};

// DELETE wrapper
export const deleteWithAuth = async (
  url: string,
  config: RequestConfig = {}
): Promise<Response> => {
  return fetchWithAuth(url, {
    method: 'DELETE',
    ...config,
  });
};

// PATCH wrapper
export const patchWithAuth = async (
  url: string,
  data?: any,
  config: RequestConfig = {}
): Promise<Response> => {
  return fetchWithAuth(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
    ...config,
  });
};

// Utility to clear access token (call this after logout)
export const clearAccessToken = (): void => {
  if (typeof window !== 'undefined') {
    // Clear OAuth token cookie
    document.cookie = `${GITHUB_ACCESS_TOKEN_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    // A scoped stream token minted for the old session must not survive
    // sign-out.
    clearStreamTokenCache();
  }
};

// Utility to get GitHub installation ID (server-side only)
export const getGitHubInstallationId = (): string | null => {
  // This function should be called server-side where cookies are accessible
  // For client-side usage, you'll need to pass this value through props or context
  return null;
};

// Utility to get GitHub token type (server-side only)
export const getGitHubTokenType = (): string | null => {
  // This function should be called server-side where cookies are accessible
  // For client-side usage, you'll need to pass this value through props or context
  return null;
};

// Function to add GitHub-specific headers to requests
export const addGitHubHeaders = (
  headers: Record<string, string>,
  installationId?: string,
  tokenType?: string
): Record<string, string> => {
  const newHeaders = { ...headers };

  if (installationId) {
    newHeaders['X-GitHub-Installation-ID'] = installationId;
  }

  if (tokenType) {
    newHeaders['X-GitHub-Token-Type'] = tokenType;
  }

  return newHeaders;
};

// Export default axios instance
export default getAxiosInstance();
